import { apiClient } from './client'

// Types matching backend schemas (src/schemas/filters.py)

export interface Jurisdiction {
  jurisdiction_uuid: string
  jurisdiction_name: string        // e.g. "CA", "Alberta"
  jurisdiction_common_name: string // e.g. "Canada (Federal)" — display name
  last_updated: string | null
}

export interface PubAuthority {
  uuid: string
  code: string
  name: string
  jurisdiction_uuids: string[]
}

export interface AltLangVersionDetails {
  alt_language_code: string | null
  alt_language_legislation_uuid: string | null
  alt_language_leg_ref: string | null
  alt_language_title: string | null
  alt_language_url_link: string | null
}

export interface DocumentOption {
  legislation_uuid: string   // primary identifier
  title: string              // used as display label
  legal_ref: string          // kept for display/reference
  language_code: string      // language code of the document
  url_link: string | null    // URL link to the document
  alt_lang_version_details: AltLangVersionDetails | null
}

export interface DocumentCountResponse {
  count: number
}

/**
 * Fetch all available jurisdictions
 * GET /api/v1/filters/jurisdictions
 */
export async function getJurisdictions(): Promise<Jurisdiction[]> {
  const response = await apiClient.get<Jurisdiction[]>('/api/v1/filters/jurisdictions')
  return response.data
}

/**
 * Fetch publishing authorities for selected jurisdictions
 * GET /api/v1/filters/pub-authorities?jurisdiction_uuids=...
 */
export async function getPubAuthorities(jurisdictionUuids: string[]): Promise<PubAuthority[]> {
  if (jurisdictionUuids.length === 0) return []

  const response = await apiClient.get<PubAuthority[]>('/api/v1/filters/pub-authorities', {
    params: { jurisdiction_uuids: jurisdictionUuids },
    paramsSerializer: { indexes: null }, // sends ?jurisdiction_uuids=a&jurisdiction_uuids=b
  })
  return response.data
}

/**
 * Fetch document metadata for specific legislation UUIDs.
 * Used to seed pre-selected documents when restoring a conversation.
 */
export async function getDocumentsByUuids(legislationUuids: string[]): Promise<DocumentOption[]> {
  if (legislationUuids.length === 0) return []
  const response = await apiClient.get<DocumentOption[]>('/api/v1/filters/documents/by-uuids', {
    params: { legislation_uuids: legislationUuids },
    paramsSerializer: { indexes: null },
  })
  return response.data
}

/** DocumentOption extended with jurisdiction_uuids, returned only by by-legal-ref. */
export interface DocumentOptionWithJurisdictions extends DocumentOption {
  jurisdiction_uuids: string[]
}

/**
 * Look up a single document by exact legal_ref.
 * Returns null if not found (404) or on any error.
 * GET /api/v1/filters/documents/by-legal-ref?legal_ref=...
 */
export type LegalRefResult =
  | { status: 'found'; doc: DocumentOptionWithJurisdictions }
  | { status: 'not_found' }
  | { status: 'restricted' }

export async function getDocumentByLegalRef(legalRef: string): Promise<LegalRefResult> {
  try {
    const response = await apiClient.get<DocumentOptionWithJurisdictions>('/api/v1/filters/documents/by-legal-ref', {
      params: { legal_ref: legalRef },
    })
    return { status: 'found', doc: response.data }
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status
    if (status === 403) return { status: 'restricted' }
    return { status: 'not_found' }
  }
}

/**
 * Fetch document options for dropdown selection
 * GET /api/v1/filters/documents/options?jurisdiction_uuids=...&pub_authority_uuids=...
 */
export async function getDocumentOptions(
  jurisdictionUuids: string[],
  pubAuthorityUuids?: string[],
  limit: number = 50,
  offset: number = 0,
): Promise<DocumentOption[]> {
  if (jurisdictionUuids.length === 0) return []

  const params: Record<string, unknown> = {
    jurisdiction_uuids: jurisdictionUuids,
    limit,
    offset,
  }
  if (pubAuthorityUuids && pubAuthorityUuids.length > 0) {
    params.pub_authority_uuids = pubAuthorityUuids
  }

  const response = await apiClient.get<DocumentOption[]>('/api/v1/filters/documents/options', {
    params,
    paramsSerializer: { indexes: null },
  })
  return response.data
}

/**
 * Search documents by title
 * GET /api/v1/filters/documents/search?jurisdiction_uuids=...&query=...
 */
export async function searchDocuments(
  jurisdictionUuids: string[],
  query: string,
  pubAuthorityUuids?: string[],
  limit: number = 50,
  offset: number = 0,
): Promise<DocumentOption[]> {
  if (jurisdictionUuids.length === 0 || !query.trim()) return []

  const params: Record<string, unknown> = {
    jurisdiction_uuids: jurisdictionUuids,
    query,
    limit,
    offset,
  }
  if (pubAuthorityUuids && pubAuthorityUuids.length > 0) {
    params.pub_authority_uuids = pubAuthorityUuids
  }

  const response = await apiClient.get<DocumentOption[]>('/api/v1/filters/documents/search', {
    params,
    paramsSerializer: { indexes: null },
  })
  return response.data
}

/**
 * Get count of documents matching filters
 * GET /api/v1/filters/documents/count?jurisdiction_uuids=...
 */
export async function getDocumentCount(
  jurisdictionUuids: string[],
  pubAuthorityUuids?: string[],
): Promise<number> {
  if (jurisdictionUuids.length === 0) return 0

  const params: Record<string, unknown> = {
    jurisdiction_uuids: jurisdictionUuids,
  }
  if (pubAuthorityUuids && pubAuthorityUuids.length > 0) {
    params.pub_authority_uuids = pubAuthorityUuids
  }

  const response = await apiClient.get<DocumentCountResponse>('/api/v1/filters/documents/count', {
    params,
    paramsSerializer: { indexes: null },
  })
  return response.data.count
}

